import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Clover24Regular, Settings24Filled, Settings24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { appInsights } from '../../AppInsights';
import { useUserContext } from '../../Contexts/UserContext';
import { AdminSettingsRoute, ExploreGptRoute, SettingsRoute } from '../../Routes';
import { useFeature, useFeatures, useGptConfigurations } from '../../store/hooks';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import AppMenuButton from './AppMenuButton';
import MenuPersona from './MenuPersona';
import { getUserInfoDebug } from '../../Utils/personaUtils';

interface MenuBottomProps {
    name?: string;
    photo?: string;
    onCloseMenuRequired?: () => void;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
    },    settingsButton: {
        width: '100%',
        justifyContent: 'flex-start',
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            ...shorthands.borderRadius(tokens.borderRadiusMedium),
        },
    },
});

const MenuBottom: FC<MenuBottomProps> = (props) => {
    const { name, photo, onCloseMenuRequired } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { gptConfigurations } = useGptConfigurations();
    const [showExploreGpt, setShowExploreGpt] = useState(false);
    const chatFeature = useFeature('chat');
    const isPersonalGptEnabled = chatFeature.personalGpt?.isEnabled ?? false;
    const { userProfile } = useUserContext();

    const { features } = useFeatures();
    const isAdminEnabled = chatFeature.isAdmin;
    
    // show SyGPT Tools button if there are any GPTs other than SyGPT
    useEffect(() => {
        setShowExploreGpt(gptConfigurations.some(gpt => !gpt.isDefault && !gpt.isPersonal) || (isPersonalGptEnabled));
    }, [gptConfigurations, isPersonalGptEnabled]);    
    
    useEffect(() => {
        console.log('isAdmin:', isAdminEnabled);
        if (showExploreGpt) {
            appInsights.trackTrace({ 
                message:'gptConfigurations in MenuBottom; isPersonalGptEnabled: ' + isPersonalGptEnabled +
                         '; ' + getUserInfoDebug(userProfile),
                properties: gptConfigurations
            });
            console.log('---');
        }
    }, [showExploreGpt, isAdminEnabled]);

    const handleSettingsClick = () => {
        navigate(SettingsRoute);
        // Close menu in mobile mode when navigating to settings
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };
    const handleAdminSettingsClick = () => {
        navigate(AdminSettingsRoute);
        // Close menu in mobile mode when navigating to settings
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };

    return (
        <div className={classes.root}>
            {/* SyGPT Tools button */}
            {showExploreGpt && (
                <FeatureHighlightPopup
                    // show popup about 3C tools, if enabled from the back-end
                    id={'syGPTTool3C'}
                    header={t('featureHighlights.header')}
                    title={t('featureHighlights.exploreGPT.title')}
                    content={t('featureHighlights.exploreGPT.content')}
                    link={t('featureHighlights.exploreGPT.link')}
                    position={'after'}
                >
                    <AppMenuButton 
                        title={'menu.exploreGpts'} 
                        label={'menu.exploreGpts'} 
                        icon={<Clover24Regular />} 
                        onClick={() => {
                            navigate(ExploreGptRoute);
                            // Close menu in mobile mode when navigating to explore GPTs
                            if (onCloseMenuRequired) {
                                onCloseMenuRequired();
                            }
                        }} 
                    />
                </FeatureHighlightPopup>              
            )}

            {/* Settings button */}
            <Button
                className={classes.settingsButton}
                appearance="transparent"
                icon={<Settings24Regular />}
                onClick={handleSettingsClick}
            >
                {t('menu.settings')}
            </Button>
            
            {/* Admin Settings button */}            
             {isAdminEnabled  && (
                <Button
                className={classes.settingsButton}
                appearance="transparent"
                icon={<Settings24Filled />}
                onClick={handleAdminSettingsClick}
            >
                {t('menu.adminsettings')}
            </Button>
        )}

            {/* <MenuQuota /> */}
            <MenuPersona
                name={name}
                photo={photo}
            />
        </div>
    );
};

export default MenuBottom;
